import {reactive} from 'vue'
import axios from "axios";
import {getPreferredLocale} from "@/utils/locale.js";

const TOKEN_KEY = 'auth.token';
const SETUP_KEY = 'auth.setup';
const REDIRECT_KEY = 'auth.redirect';

export default {
    install: (app, options) => {

        const { i18n } = options;
        const { t } = i18n.global;

        const getToken = () => localStorage.getItem(TOKEN_KEY);
        const isSetup = () => localStorage.getItem(SETUP_KEY);
        const getRedirect = () => localStorage.getItem(REDIRECT_KEY);

        let isLoggedIn = !!getToken();

        if (getToken()) {
            options.axios.defaults.headers.common['Authorization'] = `Bearer ${getToken()}`;
        }

        const auth = reactive({
                isLoggedIn() { return isLoggedIn && this.user && this.user.is_setup_complete },
                isReady: !isLoggedIn,
                user: {},
                isEmployer() {
                    return this.user.role === 'employer';
                },
                isEmployee() {
                    return this.user.role === 'employee';
                },
                isAdmin() {
                    return this.user.role === 'admin';
                },
                isTrainer() {
                    return this.user.role === 'trainer';
                },
                async loginWithToken(token) {
                    options.axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    localStorage.setItem(TOKEN_KEY, token);
                    localStorage.removeItem(SETUP_KEY);
                    isLoggedIn = true;
                    await this.fetchUser();
                },
                async login(data) {
                    return options.axios.post('/auth/login', {
                        email: data.email,
                        password: data.password
                    }).then(async (response) => {
                        options.axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                        await this.fetchUser();
                        isLoggedIn = true;
                        localStorage.setItem(TOKEN_KEY, response.data.token);
                        localStorage.removeItem(SETUP_KEY);
                        const redirect = getRedirect();
                        if (redirect) {
                            localStorage.removeItem(REDIRECT_KEY);
                            options.router.push(JSON.parse(redirect));
                        } else {
                            options.router.push({name: 'dashboard'});
                        }
                    }).catch(error => {
                        const message = error.response?.data?.message;
                        return {
                            error: message ? message : t('messages.error_try_again')
                        };
                    }).finally(() => {
                    });
                },
                async otcLogin(data) {
                    return options.axios.post('/auth/otc-login', {
                        code: data.code,
                    }).then(async (response) => {
                        options.axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                        localStorage.setItem(TOKEN_KEY, response.data.token);
                        localStorage.setItem(SETUP_KEY, true);
                        await this.fetchUser();
                        options.router.push({name: 'verification'});
                    }).catch(error => {
                        const message = error.response?.data?.message;
                        return {
                            error: message ? message : t('messages.error_try_again')
                        };
                    }).finally(() => {
                    });
                },
                async loginByTrainingCode(code, redirectRoute) {
                    return options.axios.post('/auth/training-code-login', {
                        code: code,
                    }).then(async (response) => {
                        options.axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                        localStorage.setItem(TOKEN_KEY, response.data.token);
                        await this.fetchUser();
                        isLoggedIn = true;
                    }).catch(error => {
                        if (isLoggedIn) {
                            this.logout(false)
                        }
                        localStorage.setItem(REDIRECT_KEY, JSON.stringify(redirectRoute));
                    }).finally(() => {
                    });
                },
                logout(redirect = true) {
                    options.axios.delete('/auth/logout').then(() => {}).catch();
                    isLoggedIn = false;
                    localStorage.removeItem(TOKEN_KEY);
                    localStorage.removeItem(SETUP_KEY);
                    options.axios.defaults.headers.common['Authorization'] = null;
                    redirect && options.router.push({name: 'login'});
                },
                forceLogout() {
                    isLoggedIn = false;
                    localStorage.removeItem(TOKEN_KEY);
                    options.axios.defaults.headers.common['Authorization'] = null;
                    options.router.push({name: 'login'});
                },
                async fetchUser() {
                    return options.axios.get('/auth/me').then(response => {
                        this.user = response.data.data;
                        if (!this.user.locale || this.user.locale !== getPreferredLocale()) {
                            axios.post('/profile/locale', { locale: getPreferredLocale()});
                        }
                    }).catch().finally(() => {
                        this.isReady = true;
                    });
                }
            }
        )

        options.axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.code === 'ERR_NETWORK') {
                    app.config.globalProperties.$toast.error(t('messages.network_unavailable'));
                    auth.forceLogout();
                }
                if (error.response?.status === 401) {
                    auth.forceLogout();
                }
                if (error.response?.status === 403) {
                    options.router.push({name: 'home'})
                }
                return Promise.reject(error);
            }
        );

        options.router.beforeEach(async (to, from, next) => {
            if (getToken() && !auth.user) {
                await auth.fetchUser();
            }
            if (to.meta.public) {
                next()
            } else if (!to.meta.guest && (!getToken() || isSetup()) && to.name !== 'verification') {
                next({name: 'login'})
                auth.forceLogout()
            } else if (to.meta.guest && (getToken() && !isSetup())) {
                next({name: 'dashboard'})
            } else if (to.meta.employer && auth.user.role && auth.user.role !== 'employer') {
                next({name: 'dashboard'})
            }  else if (to.meta.trainer && auth.user.role && auth.user.role !== 'trainer') {
                next({name: 'dashboard'})
            } else {
                next()
            }
        })

        if (getToken()) {
            auth.fetchUser().then(async () => {
                await options.router.isReady();
                if (options.router?.currentRoute?.value?.meta?.employer && auth.user.role !== 'employer') {
                    options.router.push({name: 'dashboard'})
                }
            });
        }

        app.config.globalProperties.$auth = auth;
    }
}
